import { create } from 'zustand';
import { createEmptyScene } from '../sceneGraph/schema';
import { migrateFieldConfigToConstraints } from '../sceneGraph/fieldConfigMigration';
import { layoutTextNode, layoutSceneGraph } from '../typography/layoutText';
import { createMeasureFn, clearMeasureCache } from '../typography/measureText';
import { applyMappingsToSceneGraph } from '../sceneGraph/fabricAdapter';
import { findOpenPlacement } from '../utils/placement';
import { computeMediumImageDimensions } from '../utils/imagePlacement';
import { normalizeKonvaImageNode } from '../api/gcpImagesApi';

let idSeq = 0;
function nextId(prefix = 'obj') {
  idSeq += 1;
  return `${prefix}_${Date.now()}_${idSeq}_${Math.random().toString(36).slice(2, 7)}`;
}

function ensureTextAutoGrow(node) {
  if (node?.type !== 'text') return node;
  const c = node.constraints || migrateFieldConfigToConstraints(node.fieldConfig);
  const overflow = c.overflowStrategy || 'wrap';
  if (c.layoutMode === 'fixedFrame') return { ...node, constraints: c };
  if (overflow === 'wrap' && c.layoutMode !== 'autoWidth' && c.layoutMode !== 'scaleDown') {
    return { ...node, constraints: { ...c, layoutMode: 'autoHeight', overflowStrategy: 'wrap' } };
  }
  return { ...node, constraints: c };
}

function normalizeTextTypography(node) {
  if (node?.type !== 'text') return node;
  const ty = node.typography || {};
  if (ty.textAlign) return node;
  return { ...node, typography: { ...ty, textAlign: 'left' } };
}

function relayout(node) {
  if (node?.type !== 'text') return node;
  return layoutTextNode(ensureTextAutoGrow(normalizeTextTypography({ ...node })), createMeasureFn());
}

function relayoutScene(scene) {
  return layoutSceneGraph({ ...scene, objects: [...scene.objects] }, createMeasureFn());
}

export const useEditorStore = create((set, get) => ({
  scene: createEmptyScene(800, 600),
  selectedId: null,
  selectedIds: [],
  editingTextId: null,

  setScene: (scene) =>
    set({
      scene: relayoutScene({
        ...scene,
        objects: (scene?.objects || []).map((o) => {
          if (o.type === 'image') return normalizeKonvaImageNode(o);
          if (o.type === 'text') return ensureTextAutoGrow(normalizeTextTypography(o));
          return o;
        }),
      }),
    }),

  /** Update artboard size without rescaling objects (template dimension sync). */
  setPageDimensions: (width, height) =>
    set((s) => ({
      scene: {
        ...s.scene,
        width: Math.max(1, Number(width) || 800),
        height: Math.max(1, Number(height) || 600),
      },
    })),

  setPageSize: (width, height) =>
    set((s) => {
      const oldW = s.scene.width;
      const oldH = s.scene.height;
      const newW = Math.max(1, Number(width) || 800);
      const newH = Math.max(1, Number(height) || 600);
      const scaleX = oldW > 0 ? newW / oldW : 1;
      const scaleY = oldH > 0 ? newH / oldH : 1;
      const objects = s.scene.objects.map((o) => {
        const t = o.transform || {};
        return {
          ...o,
          transform: {
            ...t,
            x: (t.x || 0) * scaleX,
            y: (t.y || 0) * scaleY,
            width: Math.max(1, (t.width || 100) * scaleX),
            height: Math.max(1, (t.height || 40) * scaleY),
          },
        };
      });
      return {
        scene: relayoutScene({
          ...s.scene,
          width: newW,
          height: newH,
          objects,
        }),
      };
    }),

  selectObject: (id, options = {}) => {
    if (!id) {
      set({ selectedId: null, selectedIds: [], editingTextId: null });
      return;
    }
    const additive = options.additive === true;
    set((s) => {
      let nextIds;
      if (additive) {
        nextIds = s.selectedIds.includes(id) ? s.selectedIds.filter((x) => x !== id) : [...s.selectedIds, id];
      } else {
        nextIds = [id];
      }
      return { selectedIds: nextIds, selectedId: nextIds[0] || null, editingTextId: null };
    });
  },

  setSelection: (ids) => {
    const list = Array.isArray(ids) ? [...new Set(ids.filter(Boolean))] : [];
    set({ selectedIds: list, selectedId: list[0] || null, editingTextId: null });
  },

  clearSelection: () => set({ selectedIds: [], selectedId: null, editingTextId: null }),

  startTextEdit: (id) => set({ editingTextId: id, selectedId: id, selectedIds: id ? [id] : [] }),

  endTextEdit: () => set({ editingTextId: null }),

  addTextBox: (options = {}) => {
    const scene = get().scene;
    const pageW = scene.width;
    const pageH = scene.height;
    const fontSize = options.fontSize ?? 56;
    const width = Math.min(Math.max(120, pageW * 0.55), options.width ?? 520);
    const height = Math.max(fontSize * 1.4, options.height ?? fontSize * 2);
    const id = nextId('text');
    const constraints = migrateFieldConfigToConstraints({
      behavior: 'wrap',
      layoutMode: 'fixedFrame',
      overflowStrategy: 'wrap',
      baseFontSize: fontSize,
      maxWidth: width,
      maxHeight: height,
    });
    const hasPos = options.left != null && options.top != null;
    const place = hasPos
      ? { left: options.left, top: options.top }
      : findOpenPlacement(scene, width, height);
    let node = {
      id,
      type: 'text',
      zIndex: scene.objects.length,
      text: options.text ?? 'Double-click to edit',
      transform: {
        x: hasPos ? options.left : place.left - width / 2,
        y: hasPos ? options.top : place.top - height / 2,
        width,
        height,
        rotation: options.angle ?? 0,
        scaleX: 1,
        scaleY: 1,
        originX: 'left',
        originY: 'top',
      },
      typography: {
        fontFamily: options.fontFamily || 'Arial',
        fontSize,
        fontWeight: options.fontWeight || 'normal',
        fontStyle: options.fontStyle || 'normal',
        lineHeight: 1.16,
        letterSpacing: 0,
        textAlign: options.textAlign || 'left',
        fill: options.fill || '#111827',
        underline: false,
      },
      constraints,
    };
    node = relayout(node);
    set((s) => ({
      scene: { ...s.scene, objects: [...s.scene.objects, node] },
      selectedId: id,
      selectedIds: [id],
      editingTextId: null,
    }));
    return id;
  },

  addShape: (spec = {}) => {
    const scene = get().scene;
    const style = spec;
    const cx = spec.centerX ?? scene.width / 2;
    const cy = spec.centerY ?? scene.height / 2;
    const w = spec.width ?? 120;
    const h = spec.height ?? 80;
    const hasPos = spec.left != null && spec.top != null;
    const place = hasPos
      ? findOpenPlacement(scene, w, h, { left: spec.left + w / 2, top: spec.top + h / 2 })
      : findOpenPlacement(scene, w, h);
    const isLine = spec.fabricType === 'line' || spec.fabricType === 'polyline';
    const id = nextId('shape');
    const node = {
      id,
      type: 'shape',
      zIndex: scene.objects.length,
      fabricType: spec.fabricType || 'rect',
      fill: style.fill ?? '#e2e8f0',
      stroke: style.stroke ?? '#94a3b8',
      strokeWidth: spec.strokeWidth ?? (isLine ? 3 : 2),
      strokeDashArray: spec.strokeDashArray,
      radius: spec.fabricType === 'circle' ? Math.min(w, h) / 2 : spec.radius,
      points: spec.points,
      rx: spec.rx,
      closed: spec.closed,
      transform: {
        x: hasPos ? spec.left : place.left - w / 2,
        y: hasPos ? spec.top : place.top - h / 2,
        width: w,
        height: h,
        rotation: spec.angle ?? 0,
        scaleX: 1,
        scaleY: 1,
        originX: 'left',
        originY: 'top',
      },
    };
    set((s) => ({
      scene: { ...s.scene, objects: [...s.scene.objects, node] },
      selectedId: id,
      selectedIds: [id],
    }));
    return id;
  },

  addImage: (spec = {}) => {
    const scene = get().scene;
    const pageW = scene.width;
    const pageH = scene.height;
    const { width: w, height: h } = computeMediumImageDimensions(
      spec.naturalWidth || 200,
      spec.naturalHeight || 200,
      pageW,
      pageH,
    );
    const hasCenter = spec.left != null && spec.top != null;
    const place = hasCenter
      ? findOpenPlacement(scene, w, h, { left: spec.left, top: spec.top })
      : findOpenPlacement(scene, w, h);
    const id = nextId('img');
    const node = {
      id,
      type: 'image',
      zIndex: scene.objects.length,
      src: spec.src || '',
      objectKey: spec.objectKey || spec.gcpObjectKey,
      gcpObjectKey: spec.objectKey || spec.gcpObjectKey,
      lockAspectRatio: spec.lockAspectRatio !== false,
      fieldKey: spec.fieldKey,
      transform: {
        x: place.left - w / 2,
        y: place.top - h / 2,
        width: w,
        height: h,
        rotation: 0,
        scaleX: 1,
        scaleY: 1,
        originX: 'left',
        originY: 'top',
      },
      fabricType: 'image',
    };
    set((s) => ({
      scene: { ...s.scene, objects: [...s.scene.objects, node] },
      selectedId: id,
      selectedIds: [id],
    }));
    return id;
  },

  updateObjectTransform: (id, partial) =>
    set((s) => ({
      scene: {
        ...s.scene,
        objects: s.scene.objects.map((o) => {
          if (o.id !== id) return o;
          const next = { ...o, transform: { ...o.transform, ...partial } };
          return o.type === 'text' ? relayout(next) : next;
        }),
      },
    })),

  updateShapeNode: (id, partial) =>
    set((s) => ({
      scene: {
        ...s.scene,
        objects: s.scene.objects.map((o) => {
          if (o.id !== id || o.type !== 'shape') return o;
          return { ...o, ...partial };
        }),
      },
    })),

  updateImageNode: (id, partial) =>
    set((s) => ({
      scene: {
        ...s.scene,
        objects: s.scene.objects.map((o) => {
          if (o.id !== id || o.type !== 'image') return o;
          const next = { ...o };
          if ('fieldKey' in partial) {
            if (partial.fieldKey) next.fieldKey = partial.fieldKey;
            else delete next.fieldKey;
          }
          if ('fieldConfig' in partial) {
            if (partial.fieldConfig) next.fieldConfig = partial.fieldConfig;
            else delete next.fieldConfig;
          }
          if (partial.src !== undefined) next.src = partial.src;
          if ('lockAspectRatio' in partial) next.lockAspectRatio = partial.lockAspectRatio !== false;
          return next;
        }),
      },
    })),

  updateTextNode: (id, partial) => {
    if (partial.typography) clearMeasureCache();
    set((s) => ({
      scene: {
        ...s.scene,
        objects: s.scene.objects.map((o) => {
          if (o.id !== id || o.type !== 'text') return o;
          const next = {
            ...o,
            ...(partial.text != null ? { text: partial.text } : {}),
            typography: partial.typography ? { ...o.typography, ...partial.typography } : o.typography,
            constraints: partial.constraints ? { ...o.constraints, ...partial.constraints } : o.constraints,
          };
          if ('fieldKey' in partial) {
            if (partial.fieldKey) next.fieldKey = partial.fieldKey;
            else delete next.fieldKey;
          }
          if ('fieldConfig' in partial) {
            if (partial.fieldConfig) next.fieldConfig = partial.fieldConfig;
            else delete next.fieldConfig;
          }
          return relayout(next);
        }),
      },
    }));
  },

  removeObject: (id) =>
    set((s) => {
      const nextIds = s.selectedIds.filter((x) => x !== id);
      return {
        scene: { ...s.scene, objects: s.scene.objects.filter((o) => o.id !== id) },
        selectedIds: nextIds,
        selectedId: s.selectedId === id ? nextIds[0] || null : s.selectedId,
        editingTextId: s.editingTextId === id ? null : s.editingTextId,
      };
    }),

  removeSelected: () => {
    const ids = [...get().selectedIds];
    if (!ids.length) return;
    ids.forEach((id) => get().removeObject(id));
  },

  reorderObject: (id, direction) => {
    set((s) => {
      const objs = [...s.scene.objects];
      const idx = objs.findIndex((o) => o.id === id);
      if (idx < 0) return s;
      const swap = direction === 'forward' ? idx + 1 : direction === 'backward' ? idx - 1 : direction === 'front' ? objs.length - 1 : direction === 'back' ? 0 : -1;
      if (swap < 0 || swap >= objs.length || swap === idx) return s;
      const [item] = objs.splice(idx, 1);
      objs.splice(swap, 0, item);
      return { scene: { ...s.scene, objects: objs.map((o, i) => ({ ...o, zIndex: i })) } };
    });
  },

  toggleLock: (id) =>
    set((s) => ({
      scene: {
        ...s.scene,
        objects: s.scene.objects.map((o) => (o.id === id ? { ...o, locked: !o.locked } : o)),
      },
    })),

  selectByZIndex: (zIndex) => {
    const obj = get().scene.objects.find((o) => o.zIndex === zIndex);
    if (obj) set({ selectedId: obj.id, selectedIds: [obj.id], editingTextId: null });
  },

  applyMappings: (mappings, lookupFn) => {
    const scene = JSON.parse(JSON.stringify(get().scene));
    set({ scene: applyMappingsToSceneGraph(scene, mappings, lookupFn) });
  },

  getObjectById: (id) => get().scene.objects.find((o) => o.id === id) || null,

  getSerializedScene: () => JSON.parse(JSON.stringify(get().scene)),
}));
